// Cross-domain login for the embed: opens the hosted 365 login popup, receives a single-use code
// via postMessage (validated against the login origin), swaps it for a JWT via /exchange-code, and
// handles token refresh. See AuthController.embed-auth-code / exchange-code / refresh.
import type { AuthResult, EmbedConfig } from './types';
import { SessionStore } from './store';

export class AuthRequiredError extends Error {
  constructor() {
    super('365-embed: authentication required');
    this.name = 'AuthRequiredError';
  }
}

export class Auth {
  private readonly loginOrigin: string;

  constructor(
    private cfg: EmbedConfig,
    private venueKey: string,
    private store: SessionStore,
  ) {
    this.loginOrigin = new URL(cfg.loginUrl).origin;
  }

  get token(): string | null { return this.store.accessToken; }
  get isLoggedIn(): boolean { return !!this.store.accessToken; }
  /** Signed-in user's name + email (used as booking contact info). */
  get profile(): { name: string; email: string } | null { return this.store.profile; }

  /** Ensure we have a token, opening the hosted login popup if needed. Returns false if blocked/cancelled. */
  async ensureLoggedIn(): Promise<boolean> {
    if (this.store.accessToken) return true;
    return this.login();
  }

  /**
   * Full-page redirect login (no popup). Saves an optional pending action, then navigates the whole page
   * to the hosted 365 login; the login page redirects back here with a one-time ?365code= which
   * completeReturn() swaps for a token. Credentials stay on the 365 domain — same security as the popup,
   * without the popup blocker.
   */
  redirectToLogin(pending?: unknown): void {
    if (pending !== undefined) {
      try { this.store.setPending(JSON.stringify(pending)); } catch { /* ignore */ }
    }
    const url =
      `${this.cfg.loginUrl}?key=${encodeURIComponent(this.venueKey)}` +
      `&origin=${encodeURIComponent(location.origin)}` +
      `&mode=redirect&return=${encodeURIComponent(location.href)}` +
      `&cb=${Date.now()}`; // cache-buster: a stale cached login page must never serve (it may predate SSO)
    window.location.href = url;
  }

  /** On boot, if we returned from the redirect login (?365code=), exchange it for a token and strip it. */
  async completeReturn(): Promise<void> {
    let code: string | null = null;
    try { code = new URLSearchParams(location.search).get('365code'); } catch { /* ignore */ }
    if (!code) return;
    await this.exchange(code);
    try {
      const u = new URL(location.href);
      u.searchParams.delete('365code');
      history.replaceState(null, '', u.toString()); // don't reuse a spent code on refresh
    } catch { /* ignore */ }
  }

  /** Peek the pending action saved before a redirect login (the widget resumes it post-login). */
  peekPending<T>(): T | null {
    const raw = this.store.getPending();
    if (!raw) return null;
    try { return JSON.parse(raw) as T; } catch { return null; }
  }
  clearPending(): void { this.store.clearPending(); }

  /** Open the hosted 365 login popup and complete the postMessage handoff. Must be called from a user gesture. */
  login(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const url =
        `${this.cfg.loginUrl}?key=${encodeURIComponent(this.venueKey)}` +
        `&origin=${encodeURIComponent(location.origin)}` +
        `&cb=${Date.now()}`; // cache-buster (see redirectToLogin)
      const popup = window.open(url, '365-login', 'width=480,height=720,menubar=no,toolbar=no');
      if (!popup) {
        // Popup blocked — caller surfaces an inline "click to continue" fallback.
        resolve(false);
        return;
      }

      let settled = false;
      const onMessage = async (e: MessageEvent) => {
        if (e.origin !== this.loginOrigin) return;
        const data = e.data as { type?: string; code?: string } | null;
        if (!data || data.type !== '365-embed-auth' || typeof data.code !== 'string') return;
        cleanup();
        resolve(await this.exchange(data.code));
      };

      const poll = window.setInterval(() => {
        if (popup.closed && !settled) { cleanup(); resolve(!!this.store.accessToken); }
      }, 500);

      const cleanup = () => {
        settled = true;
        window.removeEventListener('message', onMessage);
        window.clearInterval(poll);
        try { popup.close(); } catch { /* ignore */ }
      };

      window.addEventListener('message', onMessage);
    });
  }

  private async exchange(code: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.cfg.baseApiUrl}/api/auth/exchange-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) return false;
      const auth = (await res.json()) as AuthResult;
      if (!auth?.accessToken) return false;
      this.store.setSession(auth);
      return true;
    } catch {
      return false;
    }
  }

  /** Try to refresh the access token using the stored refresh token. */
  async refresh(): Promise<boolean> {
    const rt = this.store.refreshToken;
    if (!rt) return false;
    try {
      const res = await fetch(`${this.cfg.baseApiUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) { this.store.clear(); return false; }
      const auth = (await res.json()) as AuthResult;
      if (!auth?.accessToken) { this.store.clear(); return false; }
      this.store.setSession(auth);
      return true;
    } catch {
      return false;
    }
  }

  logout(): void { this.store.clear(); }
}
